import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Button, Checkbox, Divider, Form, Input, Select, Tooltip, App, Flex, Spin, } from 'antd';
import FormModal from 'components/FormModal';
import { validateBillingInfo, getCountryList, saveBillingInfo, } from 'api/billing-api';
import { useSession } from 'components/Authenticated';
import { txt } from 'libs/i18n';
import { LoadingOutlined } from '@ant-design/icons';
import { isNullish } from 'libs/utils';
import { filterOption } from 'libs/filterOption';
import styles from './style.module.less';
const { Option } = Select;
const parseAddress = (addressString) => {
    const address = {
        street: '',
        postalCode: '',
        city: '',
    };
    const splitAddress = addressString.split('\n').filter((str) => str.length > 2);
    if (splitAddress.length < 2) {
        return address;
    }
    [address.street] = splitAddress;
    splitAddress.slice(1).forEach((part) => {
        const postalCodeMatch = part.match(/^\d+\s*\d+/);
        if (postalCodeMatch !== null) {
            address.postalCode = postalCodeMatch[0].trim();
            address.city = part.slice(postalCodeMatch[0].length).trim();
        }
        else if (address.city === '') {
            address.city = part.trim();
        }
    });
    return address;
};
const BillingInfoModal = ({ isVisible, onClose, billingInfo, updateBillingInfo, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    const { session } = useSession();
    const { message } = App.useApp();
    const [form] = Form.useForm();
    const [countries, setCountries] = useState({ names: [], codes: [] });
    const [billingInfoSaving, setBillingInfoSaving] = useState(false);
    const [isCheckingVat, setIsCheckingVat] = useState(false);
    const formInitialValues = {
        phoneNumber: (_a = billingInfo === null || billingInfo === void 0 ? void 0 : billingInfo.phoneNumber) !== null && _a !== void 0 ? _a : '',
        email: (_b = billingInfo === null || billingInfo === void 0 ? void 0 : billingInfo.email) !== null && _b !== void 0 ? _b : session.login.user.email,
        name: (_c = billingInfo === null || billingInfo === void 0 ? void 0 : billingInfo.name) !== null && _c !== void 0 ? _c : '',
        companyIdentificationNumber: (_d = billingInfo === null || billingInfo === void 0 ? void 0 : billingInfo.companyIdentificationNumber) !== null && _d !== void 0 ? _d : '',
        taxIdentificationNumber: (_e = billingInfo === null || billingInfo === void 0 ? void 0 : billingInfo.taxIdentificationNumber) !== null && _e !== void 0 ? _e : '',
        reverseCharge: (_f = billingInfo === null || billingInfo === void 0 ? void 0 : billingInfo.reverseCharge) !== null && _f !== void 0 ? _f : false,
        address: (_g = billingInfo === null || billingInfo === void 0 ? void 0 : billingInfo.address) !== null && _g !== void 0 ? _g : '',
        addressComplement: (_h = billingInfo === null || billingInfo === void 0 ? void 0 : billingInfo.addressComplement) !== null && _h !== void 0 ? _h : '',
        city: (_j = billingInfo === null || billingInfo === void 0 ? void 0 : billingInfo.city) !== null && _j !== void 0 ? _j : '',
        postalCode: (_k = billingInfo === null || billingInfo === void 0 ? void 0 : billingInfo.postalCode) !== null && _k !== void 0 ? _k : '',
        country: (_l = billingInfo === null || billingInfo === void 0 ? void 0 : billingInfo.country) !== null && _l !== void 0 ? _l : '',
        countryCode: (_m = billingInfo === null || billingInfo === void 0 ? void 0 : billingInfo.countryCode) !== null && _m !== void 0 ? _m : '',
    };
    const pairFieldErrorWithMessage = (field, error) => {
        if (error === 'CannotValidate') {
            return txt('unavailableVIES');
        }
        if (error === 'Required') {
            switch (field) {
                case 'reverseCharge':
                    form.setFieldValue('reverseCharge', true);
                    return '';
                case 'countryCode':
                    form.setFieldValue('country', '');
                    return txt('requiredField');
                default:
                    return txt('requiredField');
            }
        }
        if (error === 'NotSupported') {
            if (field === 'countryCode') {
                return txt('invalidForCz');
            }
            if (field === 'reverseCharge') {
                form.setFieldValue('reverseCharge', false);
                return '';
            }
        }
        switch (field) {
            case 'phoneNumber':
                return txt('phoneFormat');
            case 'email':
                return txt('invalidEmail');
            case 'countryCode':
                return txt('invalidCountry');
            case 'companyIdentificationNumber':
                return txt('invalidCompNumber');
            case 'country':
                return txt('invalidCountry');
            case 'taxIdentificationNumber':
                form.setFieldValue('reverseCharge', false);
                return txt('invalidTin');
            default:
                return txt('invalidFormat');
        }
    };
    const validateFields = async (formValues) => {
        const result = await validateBillingInfo(session.connection, formValues);
        const formFields = Object.keys(formValues);
        formFields.forEach((key) => {
            form.setFields([
                {
                    name: key === 'countryCode' ? 'country' : key,
                    errors: result.isFail() && Object.keys(result.err().fields).includes(key)
                        ? [pairFieldErrorWithMessage(key, result.err().fields[key])]
                        : [],
                },
            ]);
        });
        return result;
    };
    useEffect(() => {
        const fetchCountryList = async () => {
            const countryList = await getCountryList(session.connection);
            setCountries({
                names: countryList.map((item) => item.nativeName),
                codes: countryList.map((item) => item.code),
            });
        };
        void fetchCountryList();
    }, []);
    useEffect(() => {
        if (isVisible) {
            form.setFieldsValue(Object.assign({}, formInitialValues));
            void validateFields(form.getFieldsValue());
        }
    }, [isVisible]);
    const handleSubmit = async () => {
        const formValues = form.getFieldsValue();
        setBillingInfoSaving(true);
        const validationResult = await validateFields(formValues);
        if (validationResult.isFail()) {
            setBillingInfoSaving(false);
            return;
        }
        const result = await saveBillingInfo(session.connection, formValues);
        if (result.isSuccess()) {
            void message.success(txt('changesSaved'), 2);
            updateBillingInfo();
            setBillingInfoSaving(false);
            onClose();
        }
        else {
            setBillingInfoSaving(false);
            void message.error(txt('failedToSave'));
        }
    };
    const fillEmptyCompanyFields = (companyDetails) => {
        const { name, address } = companyDetails;
        if (form.getFieldValue('name') === '' && name !== '---') {
            form.setFieldValue('name', companyDetails.name);
        }
        if (form.getFieldValue('address') === '' && address !== '---') {
            const { street, postalCode, city } = parseAddress(address);
            form.setFieldsValue({ address: street, postalCode, city });
        }
    };
    const validateVatAndFillCompanyFields = async () => {
        await form.validateFields(['country']);
        setIsCheckingVat(true);
        const result = await validateFields(form.getFieldsValue());
        setIsCheckingVat(false);
        if (result.isFail() && !isNullish(result.err().data)) {
            const companyDetails = result.err().data;
            if (companyDetails.valid === true) {
                fillEmptyCompanyFields(companyDetails);
                form.setFieldValue('reverseCharge', companyDetails.reverseChargeSuggestion);
            }
        }
        else if (result.isSuccess()) {
            fillEmptyCompanyFields(result.get());
            form.setFieldValue('reverseCharge', result.get().reverseChargeSuggestion);
        }
    };
    const handleSelectChange = (value, option) => {
        form.setFieldsValue({ countryCode: option.value, country: option.children });
        void validateVatAndFillCompanyFields();
    };
    return (_jsxs(FormModal, { className: styles.billingInfoModal, open: isVisible, style: { top: 0 }, width: 600, onCancel: onClose, footer: [
            _jsx(Button, { onClick: onClose, children: txt('cancel') }, "submit"),
            _jsx(Button, { type: "primary", loading: billingInfoSaving, disabled: isCheckingVat, onClick: handleSubmit, children: txt('save') }, "back"),
        ], children: [_jsx("h2", { children: txt('billingInfo') }), _jsxs(Form, { name: "billingInfo", form: form, initialValues: formInitialValues, onFinish: handleSubmit, scrollToFirstError: true, layout: "vertical", children: [_jsxs("div", { children: [_jsx(Spin, { spinning: isCheckingVat && ((_o = form.getFieldValue('company')) === null || _o === void 0 ? void 0 : _o.length) === 0, tip: "Validating TIN and fetching company details", children: _jsx(Form.Item, { label: txt('nameOrCompany'), name: "name", required: true, children: _jsx(Input, {}) }) }), _jsx(Form.Item, { label: txt('email'), name: "email", required: true, children: _jsx(Input, {}) }), _jsx(Form.Item, { label: txt('phone'), name: "phoneNumber", children: _jsx(Input, { placeholder: txt('phoneExample') }) }), _jsx(Form.Item, { name: "country", label: txt('country'), required: true, children: _jsx(Select, { showSearch: true, placeholder: txt('chooseCountry'), filterOption: filterOption, onChange: handleSelectChange, children: countries.names.map((name, code) => (_jsx(Option, { value: countries.codes[code], label: name, children: name }, name))) }) }), _jsx(Form.Item, { className: styles.hidden, name: "countryCode", children: _jsx(Input, {}) }), _jsx(Divider, { className: styles.divider, children: txt('otherDetails') }), _jsxs("div", { className: styles.ids, children: [_jsx(Form.Item, { label: txt('taxIdNumber'), name: "taxIdentificationNumber", children: _jsx(Input, { onBlur: () => validateVatAndFillCompanyFields() }) }), _jsx(Form.Item, { label: txt('idNumber'), name: "companyIdentificationNumber", children: _jsx(Input, {}) })] }), isCheckingVat ? _jsx(LoadingOutlined, {}) : (_jsx(Tooltip, { title: txt('automaticFill'), children: _jsx(Form.Item, { name: "reverseCharge", valuePropName: "checked", children: _jsx(Checkbox, { disabled: true, id: "reverseCharge", children: txt('reverseCharge') }) }) }))] }), _jsxs(Spin, { spinning: isCheckingVat && ((_p = form.getFieldValue('address')) === null || _p === void 0 ? void 0 : _p.length) === 0, tip: "Validating TIN and fetching company details", children: [_jsxs(Flex, { className: styles.addressInputs, align: "end", wrap: "wrap", gap: "small", justify: "space-between", children: [_jsx(Form.Item, { name: "address", className: styles.addressInputsText, label: `${txt('street')}, ${txt('stNumber')}`, children: _jsx(Input, {}) }), _jsx(Form.Item, { name: "addressComplement", className: styles.addressInputsNumber, label: txt('addressComplement'), children: _jsx(Input, {}) })] }), _jsxs(Flex, { align: "end", className: styles.addressInputs, wrap: "wrap", gap: "small", justify: "space-between", children: [_jsx(Form.Item, { name: "city", className: styles.addressInputsText, label: txt('city'), children: _jsx(Input, {}) }), _jsx(Form.Item, { name: "postalCode", className: styles.addressInputsNumber, label: txt('postcode'), children: _jsx(Input, {}) })] })] })] })] }));
};
export default BillingInfoModal;
